// Terms Agreement Management Utility
// Handles storing and retrieving user's agreement to terms and conditions

const TERMS_AGREEMENT_KEY = 'terms_agreement_status';
const TERMS_AGREEMENT_TIMESTAMP = 'terms_agreement_timestamp';

export interface TermsAgreementStatus {
  hasAgreed: boolean;
  timestamp: number;
  version: string;
  browserUserAgent?: string;
  browserLanguage?: string;
  browserPlatform?: string;
  consentData?: Record<string, any>;
}

// Get browser information
const getBrowserInfo = () => {
  try {
    return {
      userAgent: navigator.userAgent || '',
      language: navigator.language || '',
      platform: navigator.platform || '',
      consentData: {
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack || 'unknown'
      }
    };
  } catch (error) {
    console.warn('Failed to get browser info:', error);
    return {
      userAgent: '',
      language: '',
      platform: '',
      consentData: {}
    };
  }
};

// Dispatch custom event to notify components of status change
const notifyTermsAgreementChange = () => {
  try {
    window.dispatchEvent(new CustomEvent('termsAgreementChanged'));
  } catch (error) {
    console.warn('Failed to dispatch terms agreement change event:', error);
  }
};

export const getTermsAgreementStatus = (): TermsAgreementStatus => {
  try {
    const stored = sessionStorage.getItem(TERMS_AGREEMENT_KEY);
    const timestamp = sessionStorage.getItem(TERMS_AGREEMENT_TIMESTAMP);
    const version = sessionStorage.getItem('terms_agreement_version') || '1.0';
    const userAgent = sessionStorage.getItem('terms_agreement_user_agent') || '';
    const language = sessionStorage.getItem('terms_agreement_language') || '';
    const platform = sessionStorage.getItem('terms_agreement_platform') || '';
    const consentData = sessionStorage.getItem('terms_agreement_consent_data');
    
    if (stored && timestamp) {
      const parsedTimestamp = parseInt(timestamp, 10);
      
      // Validate timestamp is reasonable
      if (parsedTimestamp > 0 && !isNaN(parsedTimestamp)) {
        return {
          hasAgreed: JSON.parse(stored),
          timestamp: parsedTimestamp,
          version: version,
          browserUserAgent: userAgent,
          browserLanguage: language,
          browserPlatform: platform,
          consentData: consentData ? JSON.parse(consentData) : {}
        };
      } else {
        console.warn('⚠️ Invalid timestamp in session storage:', timestamp);
      }
    }
  } catch (error) {
    console.warn('Failed to parse terms agreement status:', error);
  }
  
  return {
    hasAgreed: false,
    timestamp: 0,
    version: '1.0',
    browserUserAgent: '',
    browserLanguage: '',
    browserPlatform: '',
    consentData: {}
  };
};

export const setTermsAgreementStatus = (hasAgreed: boolean): void => {
  try {
    const browserInfo = getBrowserInfo();
    const timestamp = Date.now();
    
    // Validate timestamp is reasonable
    if (timestamp <= 0 || isNaN(timestamp)) {
      console.error('❌ Invalid timestamp generated:', timestamp);
      return;
    }
    
    sessionStorage.setItem(TERMS_AGREEMENT_KEY, JSON.stringify(hasAgreed));
    sessionStorage.setItem(TERMS_AGREEMENT_TIMESTAMP, timestamp.toString());
    sessionStorage.setItem('terms_agreement_version', '1.0');
    sessionStorage.setItem('terms_agreement_user_agent', browserInfo.userAgent);
    sessionStorage.setItem('terms_agreement_language', browserInfo.language);
    sessionStorage.setItem('terms_agreement_platform', browserInfo.platform);
    sessionStorage.setItem('terms_agreement_consent_data', JSON.stringify(browserInfo.consentData));
    
    // Debug logging
    console.log('✅ Terms agreement status set:', {
      hasAgreed,
      timestamp,
      version: '1.0',
      browserInfo
    });
    
    // Notify all components of the change
    notifyTermsAgreementChange();
  } catch (error) {
    console.warn('Failed to store terms agreement status:', error);
  }
};

export const clearTermsAgreementStatus = (): void => {
  try {
    sessionStorage.removeItem(TERMS_AGREEMENT_KEY);
    sessionStorage.removeItem(TERMS_AGREEMENT_TIMESTAMP);
    sessionStorage.removeItem('terms_agreement_version');
    sessionStorage.removeItem('terms_agreement_user_agent');
    sessionStorage.removeItem('terms_agreement_language');
    sessionStorage.removeItem('terms_agreement_platform');
    sessionStorage.removeItem('terms_agreement_consent_data');
    
    // Notify all components of the change
    notifyTermsAgreementChange();
  } catch (error) {
    console.warn('Failed to clear terms agreement status:', error);
  }
};

export const hasUserAgreedToTerms = (): boolean => {
  return getTermsAgreementStatus().hasAgreed;
};

// Check if agreement is still valid (e.g., if terms have been updated)
export const isTermsAgreementValid = (): boolean => {
  const status = getTermsAgreementStatus();
  
  // For now, we'll consider it valid if they agreed in the current session
  // In the future, you could add version checking here
  return status.hasAgreed && status.timestamp > 0;
};
